import React, { useState } from 'react';
import { Icon } from './icons';
import { AWS_CONFIG } from '../config/aws';

const ICON_OPTIONS = [
  'Sparkles', 'Wind', 'Droplets', 'SprayCan', 'Brush',
  'Car', 'Shield', 'Star', 'Zap', 'Wrench', 'CircleDashed',
];

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 100, padding: 20,
};
const cardStyle = { padding: 24, maxWidth: 480, width: '100%', maxHeight: '90vh', overflowY: 'auto' };
const labelStyle = { fontSize: '0.85rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 };

function IconPicker({ value, onChange }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(56px, 1fr))', gap: 6 }}>
      {ICON_OPTIONS.map(name => (
        <button key={name} type="button" onClick={() => onChange(name)} title={name}
          style={{
            padding: '10px', borderRadius: 'var(--radius)',
            border: '1px solid', borderColor: value === name ? 'var(--primary)' : 'var(--border)',
            background: value === name ? 'rgba(74,159,255,0.1)' : 'var(--surface)',
            color: value === name ? 'var(--primary)' : 'var(--text-muted)',
            cursor: 'pointer', display: 'flex', justifyContent: 'center',
          }}>
          <Icon name={name} size={22} />
        </button>
      ))}
    </div>
  );
}

export function AddServiceModal({ onClose, onCreated, existingIds, getIdToken }) {
  const [form, setForm] = useState({ serviceId: '', name: '', description: '', icon: 'Sparkles' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const update = (f) => (e) => setForm({ ...form, [f]: e.target.value });
  const canSubmit =
    /^[a-z0-9][a-z0-9-]{1,30}$/.test(form.serviceId) &&
    form.name.trim() &&
    !existingIds.includes(form.serviceId) &&
    !submitting;

  const handleSubmit = async () => {
    setSubmitting(true); setError('');
    try {
      const token = await getIdToken();
      const res = await fetch(`${AWS_CONFIG.apiUrl}/services`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || `생성 실패 (${res.status})`);
      } else { onCreated(); }
    } catch (err) { setError(err.message || '서버 연결 실패'); }
    setSubmitting(false);
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div className="card" onClick={e => e.stopPropagation()} style={cardStyle}>
        <h2 style={{ marginTop: 0, marginBottom: 16 }}>새 서비스 추가</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={labelStyle}>
              서비스 ID * <span style={{ color: 'var(--text-dim)', fontWeight: 400 }}>(소문자/숫자/하이픈, 예: polish-wax)</span>
            </label>
            <input className="input" value={form.serviceId} onChange={update('serviceId')} placeholder="polish-wax" autoFocus />
          </div>
          <div>
            <label style={labelStyle}>이름 *</label>
            <input className="input" value={form.name} onChange={update('name')} placeholder="광택 왁스" />
          </div>
          <div>
            <label style={labelStyle}>설명 (선택)</label>
            <input className="input" value={form.description} onChange={update('description')} placeholder="프리미엄 왁스 도포로 광택 유지" />
          </div>
          <div>
            <label style={labelStyle}>아이콘</label>
            <IconPicker value={form.icon} onChange={(name) => setForm({ ...form, icon: name })} />
          </div>
          {error && <p style={{ color: '#ff5252', fontSize: '0.85rem', margin: 0 }}>{error}</p>}
          {form.serviceId && existingIds.includes(form.serviceId) && (
            <p style={{ color: '#ff9800', fontSize: '0.85rem', margin: 0 }}>이미 존재하는 ID입니다</p>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="btn btn-outline" onClick={onClose} style={{ flex: 1 }}>취소</button>
            <button className="btn btn-primary" disabled={!canSubmit} onClick={handleSubmit}
              style={{ flex: 2, opacity: canSubmit ? 1 : 0.5 }}>
              {submitting ? '생성 중...' : '추가 (가격은 0으로 시작)'}
            </button>
          </div>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-dim)', margin: 0 }}>
            * 추가 후 각 차종별 가격은 아래 표에서 입력하고 저장하세요
          </p>
        </div>
      </div>
    </div>
  );
}

export function EditServiceModal({ service, onClose, onSaved, getIdToken }) {
  const [form, setForm] = useState({
    name: service.name || '',
    description: service.description || '',
    icon: service.icon || 'Sparkles',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const update = (f) => (e) => setForm({ ...form, [f]: e.target.value });
  const canSubmit = form.name.trim() && !submitting;

  const handleSubmit = async () => {
    setSubmitting(true); setError('');
    try {
      const token = await getIdToken();
      const res = await fetch(`${AWS_CONFIG.apiUrl}/services/${service.serviceId}/info`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || `저장 실패 (${res.status})`);
      } else { onSaved(); }
    } catch (err) { setError(err.message || '서버 연결 실패'); }
    setSubmitting(false);
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div className="card" onClick={e => e.stopPropagation()} style={cardStyle}>
        <h2 style={{ marginTop: 0, marginBottom: 16 }}>서비스 정보 수정</h2>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginTop: 0, marginBottom: 16 }}>
          ID: <code>{service.serviceId}</code> (변경 불가)
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={labelStyle}>이름 *</label>
            <input className="input" value={form.name} onChange={update('name')} autoFocus />
          </div>
          <div>
            <label style={labelStyle}>설명</label>
            <input className="input" value={form.description} onChange={update('description')} />
          </div>
          <div>
            <label style={labelStyle}>아이콘</label>
            <IconPicker value={form.icon} onChange={(name) => setForm({ ...form, icon: name })} />
          </div>
          {error && <p style={{ color: '#ff5252', fontSize: '0.85rem', margin: 0 }}>{error}</p>}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="btn btn-outline" onClick={onClose} style={{ flex: 1 }}>취소</button>
            <button className="btn btn-primary" disabled={!canSubmit} onClick={handleSubmit}
              style={{ flex: 2, opacity: canSubmit ? 1 : 0.5 }}>
              {submitting ? '저장 중...' : '저장'}
            </button>
          </div>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-dim)', margin: 0 }}>
            * 가격 변경은 아래 표에서 별도로 진행하세요
          </p>
        </div>
      </div>
    </div>
  );
}
